import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadSimple, FileText, CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { ProgressDonut, useSimulatedProgress } from "@/components/ProgressDonut";
import { C } from "@/components/fm/shared";
import FmInterview from "@/components/fm/FmInterview";
import { INDUSTRY_CARDS, MODEL_PICK_LABELS, OTHER_REVENUE_OPTIONS } from "@/lib/fmInterview";

const DavidBubble = ({ children }) => (
  <div className="flex items-start gap-3 mb-8">
    <div className="w-9 h-9 rounded-full grid place-items-center shrink-0 font-extrabold text-sm"
      style={{ backgroundColor: C.teal, color: "#fff" }}>D</div>
    <div className="rounded-2xl rounded-tl-sm px-5 py-4 max-w-2xl"
      style={{ backgroundColor: C.panel, border: "1px solid rgba(123,211,206,0.15)" }}>
      {children}
    </div>
  </div>
);

const blankLocal = () => ({
  profile: { revenue_status: "", funding_stage: "", target_funder_type: "" },
  assumptions: { revenue: {}, costs: {}, capital: {}, scenarios: {} },
  milestones: [],
});

export default function FinModelNew() {
  const navigate = useNavigate();
  const [stage, setStage] = useState("industry"); // industry | modelpick | docs | interview
  const [industry, setIndustry] = useState(null);
  const [otherText, setOtherText] = useState("");
  const [revenueModel, setRevenueModel] = useState(null);
  const [model, setModel] = useState(blankLocal());
  const [mode, setMode] = useState("local");
  const [uploading, setUploading] = useState(false);
  const readPct = useSimulatedProgress(uploading, { cap: 94, tau: 14000 });
  const fileRef = useRef(null);

  const pickIndustry = (card) => {
    setIndustry(card);
    if (card.key === "other") return; // wait for text + continue
    if (card.models.length === 1) { setRevenueModel(card.models[0]); setStage("docs"); }
    else setStage("modelpick");
  };

  const confirmOther = () => {
    if (!otherText.trim()) return toast.error("Tell me your industry in a few words");
    setStage("modelpick");
  };

  const pickModel = (m) => { setRevenueModel(m); setStage("docs"); };

  const baseProfile = () => ({
    sector: industry?.sector || "Other",
    industry_key: industry?.key || "other",
    industry_other: industry?.key === "other" ? otherText.trim() : "",
    revenue_model: revenueModel,
    country: "Kenya",
  });

  /* Skip path — model is created after Section 1 answers are confirmed */
  const createFromLocal = async (localModel) => {
    const { data } = await api.post("/fm/models", {
      profile: { ...baseProfile(), ...localModel.profile },
    });
    setModel({ ...data, _local: localModel._local });
    setMode("live");
    return data;
  };

  /* Upload path — /fm/import creates the model, then we align profile + apply */
  const uploadDocs = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.slice(0, 5).forEach((f) => fd.append("files", f));
      const { data } = await api.post("/fm/import", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      const fmId = data.fm_id;
      await api.put(`/fm/models/${fmId}`, { profile: baseProfile() });
      try { await api.post(`/fm/models/${fmId}/import/apply`, { answers: {} }); } catch { /* still usable */ }
      const fresh = await api.get(`/fm/models/${fmId}`);
      setModel(fresh.data);
      setMode("live");
      setStage("interview");
      const nQs = (fresh.data.import_session?.questions || []).length;
      toast.success("I read your documents and pre-filled what I could", {
        description: nQs ? `I'll double-check ${nQs} unclear figure${nQs > 1 ? "s" : ""} as we go — confirm or correct each pre-filled answer.` : "Confirm or correct each pre-filled answer as we go.",
        duration: 9000,
      });
    } catch (err) {
      // 422 validation errors arrive as an array of objects — never pass
      // those straight to toast (React can't render them).
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d) => d?.msg).filter(Boolean).join(" · ")
          : "";
      toast.error(msg || "I couldn't read those documents — try a clearer .xlsx or .pdf export");
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="min-h-screen pb-24 text-white" style={{ backgroundColor: C.bg, fontFamily: "Manrope, sans-serif" }} data-testid="fm-new-page">
      <header className="px-6 py-4 flex items-center justify-between">
        <Link to="/dashboard" className="text-sm text-white/60 hover:text-white" data-testid="fm-new-cancel">← Dashboard</Link>
        <div className="text-[10px] uppercase tracking-[0.25em] font-bold" style={{ color: C.orange }}>Financial Model Builder</div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* STEP 1 — INDUSTRY SELECTION */}
        {stage === "industry" && (
          <div data-testid="fm-stage-industry">
            <DavidBubble>
              <div className="text-lg font-extrabold text-white">What industry are you building in?</div>
              <div className="text-[13px] text-white/60 mt-2">This helps me load the right financial model structure for you.</div>
            </DavidBubble>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {INDUSTRY_CARDS.map((card) => (
                <button key={card.key} onClick={() => pickIndustry(card)} data-testid={`fm-industry-${card.key}`}
                  className="text-left p-5 rounded-xl border-2 transition-all hover:-translate-y-0.5"
                  style={{
                    backgroundColor: industry?.key === card.key ? "rgba(44,197,168,0.08)" : "rgba(255,255,255,0.025)",
                    borderColor: industry?.key === card.key ? C.teal : "rgba(255,255,255,0.08)",
                  }}>
                  <div className="font-extrabold text-[15px]">{card.label}</div>
                </button>
              ))}
            </div>
            {industry?.key === "other" && (
              <div className="mt-6 max-w-md" data-testid="fm-industry-other-panel">
                <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-1.5">What's your industry?</div>
                <input type="text" value={otherText} onChange={(e) => setOtherText(e.target.value)}
                  data-testid="fm-industry-other-input" placeholder="e.g. Event management"
                  className="w-full px-3 py-2.5 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none"
                  style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }} />
                <button onClick={confirmOther} data-testid="fm-industry-other-continue"
                  className="mt-3 px-5 py-2.5 rounded-lg font-bold text-sm inline-flex items-center gap-2"
                  style={{ backgroundColor: C.teal, color: "#fff" }}>
                  Continue <ArrowRight size={14} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* STEP 1b — revenue model disambiguation */}
        {stage === "modelpick" && (
          <div data-testid="fm-stage-modelpick">
            <DavidBubble>
              <div className="text-lg font-extrabold text-white">
                {industry?.key === "other"
                  ? "How do you earn revenue — subscriptions, one-off sales, contracts, or transactions?"
                  : `${industry?.label} businesses earn money in a couple of ways — which sounds like you?`}
              </div>
              <div className="text-[13px] text-white/60 mt-2">This picks the revenue formulas your model runs on.</div>
            </DavidBubble>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
              {(industry?.key === "other"
                ? OTHER_REVENUE_OPTIONS
                : industry.models.map((m) => ({ v: m, ...MODEL_PICK_LABELS[m] }))
              ).map((o) => (
                <button key={o.v} onClick={() => pickModel(o.v)} data-testid={`fm-modelpick-${o.v}`}
                  className="text-left p-5 rounded-xl border-2 transition-all hover:-translate-y-0.5"
                  style={{ backgroundColor: "rgba(255,255,255,0.025)", borderColor: "rgba(255,255,255,0.1)" }}>
                  <div className="font-extrabold text-[15px]">{o.label}</div>
                  <div className="text-[12px] text-white/50 mt-1">{o.sub}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 2 — DOCUMENT UPLOAD */}
        {stage === "docs" && (
          <div data-testid="fm-stage-docs">
            <DavidBubble>
              <div className="text-lg font-extrabold text-white">
                Before we start, do you have any existing financials you can share?
              </div>
              <div className="text-[13px] text-white/60 mt-2 leading-relaxed">
                Things like a P&amp;L, management accounts, revenue records, or contracts help me pre-fill answers
                for you and save time. I'll tell you exactly which answers came from your documents so you can
                confirm or correct each one.
              </div>
            </DavidBubble>
            <input ref={fileRef} type="file" multiple accept=".pdf,.xlsx,.xlsm,.csv,.txt"
              onChange={(e) => uploadDocs(e.target.files)} style={{ display: "none" }} data-testid="fm-docs-input" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
              <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="fm-docs-upload"
                className="text-left p-6 rounded-xl border-2 transition-all hover:-translate-y-0.5 disabled:opacity-90"
                style={{ backgroundColor: "rgba(10,139,133,0.1)", borderColor: "rgba(10,139,133,0.5)" }}>
                {uploading ? (
                  <div className="mb-3" data-testid="fm-docs-progress">
                    <ProgressDonut pct={readPct} size={64} stroke={7} testid="fm-docs-progress-donut" />
                  </div>
                ) : (
                  <UploadSimple size={22} style={{ color: C.tealSoft }} className="mb-3" />
                )}
                <div className="font-extrabold text-[15px]">{uploading ? `Reading your documents… ${readPct}%` : "Upload documents"}</div>
                <div className="text-[12px] text-white/50 mt-1">
                  {uploading ? "David is extracting revenue, costs and headcount" : "PDF, Excel (.xlsx) or CSV · up to 5 files · 15MB each"}
                </div>
              </button>
              <button onClick={() => setStage("interview")} disabled={uploading} data-testid="fm-docs-skip"
                className="text-left p-6 rounded-xl border-2 transition-all hover:-translate-y-0.5 disabled:opacity-40"
                style={{ backgroundColor: "rgba(255,255,255,0.025)", borderColor: "rgba(255,255,255,0.1)" }}>
                <FileText size={22} className="mb-3 text-white/50" />
                <div className="font-extrabold text-[15px]">Skip — I'll answer from scratch</div>
                <div className="text-[12px] text-white/50 mt-1">David asks one question at a time, in plain language</div>
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 — Q&A INTERVIEW */}
        {stage === "interview" && (
          <FmInterview
            model={model}
            setModel={setModel}
            mode={mode}
            onCreateModel={createFromLocal}
            startSection={1}
            onDone={() => navigate(`/fm/${model.fm_id}`)}
          />
        )}

        {/* trust strip */}
        {stage !== "interview" && (
          <div className="mt-14 flex items-center gap-2 text-[11px] text-white/35">
            <CheckCircle size={13} style={{ color: C.tealSoft }} />
            Clinq 3+5-year methodology · every output is a formula · CRS-scored for funders
          </div>
        )}
      </main>
    </div>
  );
}
